import { Component } from '@theme/component';

/**
 * @typedef {object} VariantDropdownRefs
 * @property {HTMLElement} dropdown
 * @property {HTMLButtonElement} summary
 * @property {HTMLElement} panel
 * @property {HTMLElement} value
 * @property {HTMLFieldSetElement[]} fieldsets
 */

/**
 * Custom variant picker dropdown — toggles open/closed state and syncs the
 * selected option label with the radio input chosen inside the panel.
 *
 * The radio inputs themselves are observed by the parent <variant-picker>
 * which handles variant fetching, URL state and morphing.
 *
 * @extends {Component<VariantDropdownRefs>}
 */
class VariantDropdown extends Component {
  #closeRequest;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#handleDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleDocumentClick);

    if (this.#closeRequest) {
      window.clearTimeout(this.#closeRequest);
      this.#closeRequest = undefined;
    }
  }

  get isOpen() {
    return this.refs.dropdown?.classList.contains('is-open') ?? false;
  }

  toggle = (event) => {
    event.preventDefault();
    this.isOpen ? this.#close() : this.#open();
  };

  handleKeydown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.#close();
      this.refs.summary?.focus();
    }
  };

  handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

    if (this.refs.value) {
      this.refs.value.textContent = target.value;
    }

    if (!this.isOpen) return;

    if (this.#closeRequest) window.clearTimeout(this.#closeRequest);
    this.#closeRequest = window.setTimeout(() => {
      this.#close();
      this.#closeRequest = undefined;
    }, 150);
  };

  #open = () => {
    this.refs.dropdown?.classList.add('is-open');
    this.refs.summary?.setAttribute('aria-expanded', 'true');
  };

  #close = () => {
    this.refs.dropdown?.classList.remove('is-open');
    this.refs.summary?.setAttribute('aria-expanded', 'false');
  };

  #handleDocumentClick = (event) => {
    if (!this.isOpen) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#close();
  };
}

if (!customElements.get('variant-dropdown')) {
  customElements.define('variant-dropdown', VariantDropdown);
}
